import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminBankSettingsTab({ token }) {
  const [form, setForm] = useState({
    eur_iban: "", eur_bic: "", huf_iban: "", huf_bic: "",
    eur_huf_rate: 400, payment_reference_hint: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/bank`)
      .then(({ data }) => setForm({
        eur_iban: data.eur_iban || "",
        eur_bic: data.eur_bic || "",
        huf_iban: data.huf_iban || "",
        huf_bic: data.huf_bic || "",
        eur_huf_rate: data.eur_huf_rate || 400,
        payment_reference_hint: data.payment_reference_hint || "",
      }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/bank?token=${encodeURIComponent(token)}`, form);
      toast.success("Banki adatok mentve");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>;
  }

  return (
    <div data-testid="bank-panel">
      <div className="flex items-center gap-2 mb-5">
        <Landmark className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="text-lg sm:text-xl font-semibold text-white">Banki / Fizetési adatok</h2>
      </div>
      <p className="text-xs text-white/40 mb-6 max-w-2xl">
        Ezek az adatok jelennek meg a vevőknek a CheckoutSuccess oldalon a „Fennmaradó összeg rendezése" lépésnél.
        Ha üresen hagyod, a vevő egy „— admin tölti fel —" placeholder-t fog látni.
      </p>

      <form onSubmit={handleSave} className="space-y-6 max-w-2xl">
        <div className="bg-[#0A0A0A] border border-blue-400/15 rounded-2xl p-4 sm:p-5 space-y-3">
          <h3 className="text-sm font-semibold text-blue-300 mb-2">EUR utalás (Osztrák számla)</h3>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">IBAN</label>
            <input
              type="text"
              value={form.eur_iban}
              onChange={(e) => setForm({ ...form, eur_iban: e.target.value })}
              placeholder="AT12 XXXX XXXX XXXX XXXX"
              className="w-full bg-white/5 border border-white/10 rounded-lg p-2.5 text-sm text-white font-mono focus:border-blue-400/50 focus:outline-none"
              data-testid="bank-eur-iban-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">BIC / SWIFT</label>
            <input
              type="text"
              value={form.eur_bic}
              onChange={(e) => setForm({ ...form, eur_bic: e.target.value })}
              placeholder="XXXXATWW"
              className="w-full bg-white/5 border border-white/10 rounded-lg p-2.5 text-sm text-white font-mono focus:border-blue-400/50 focus:outline-none"
              data-testid="bank-eur-bic-input"
            />
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 rounded-2xl p-4 sm:p-5 space-y-3">
          <h3 className="text-sm font-semibold text-[#D4AF37] mb-2">HUF utalás (Magyar számla)</h3>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">IBAN</label>
            <input
              type="text"
              value={form.huf_iban}
              onChange={(e) => setForm({ ...form, huf_iban: e.target.value })}
              placeholder="HU12 XXXX XXXX XXXX XXXX"
              className="w-full bg-white/5 border border-white/10 rounded-lg p-2.5 text-sm text-white font-mono focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="bank-huf-iban-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">BIC / SWIFT</label>
            <input
              type="text"
              value={form.huf_bic}
              onChange={(e) => setForm({ ...form, huf_bic: e.target.value })}
              placeholder="XXXXHUHB"
              className="w-full bg-white/5 border border-white/10 rounded-lg p-2.5 text-sm text-white font-mono focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="bank-huf-bic-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">EUR/HUF árfolyam</label>
            <input
              type="number"
              step="0.01"
              value={form.eur_huf_rate}
              onChange={(e) => setForm({ ...form, eur_huf_rate: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
              data-testid="bank-rate-input"
            />
            <p className="text-[10px] text-white/30 mt-1">1 EUR = ? Ft (a vevő ennek alapján látja a forintos összeget)</p>
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Közlemény útmutató (vevőnek mutatott szöveg)</label>
          <textarea
            value={form.payment_reference_hint}
            onChange={(e) => setForm({ ...form, payment_reference_hint: e.target.value })}
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:border-[#D4AF37]/50 focus:outline-none"
            placeholder="Kérjük az utalás közleményében tüntesse fel a rendelés azonosítóját."
            data-testid="bank-reference-hint-input"
          />
        </div>

        <Button type="submit" disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#B8860B] font-semibold" data-testid="bank-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Mentés
        </Button>
      </form>
    </div>
  );
}
